import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { orderCustomerName, type Order } from '@/lib/orders-client';
import { channelLabelKey, isSellChannel } from '@/types/sell-channel';
import {
  itemsDiscountExcludingLoyalty,
  loyaltyRedemptionLabelKey,
  loyaltyRedemptionOf,
  shortTraceId,
} from '@/lib/loyalty-redemption';

/**
 * T31's `print/receipt` quick action: the counter ticket.
 *
 * Rendered off-screen (`hidden`) and brought back only for the print medium, where the
 * `@media print` block in `globals.css` blanks the rest of the tab — so the operator gets the
 * ticket alone, without the board, the dialog or the browser chrome around it. Deliberately plain
 * black-on-white: this is paper, so the theme (and every semantic token with it) is irrelevant; the
 * TRIGGERS that open it are ordinary buttons and read in both modes.
 *
 * Mount it with `order` set to print, and it prints once and calls `onPrinted` so the host can
 * clear its state. `window.print()` blocks until the browser's dialog is dismissed, which is why
 * nothing here needs to wait for it.
 *
 * Portalled onto `document.body` so no host's `overflow-hidden` or positioned ancestor can clip a
 * ticket longer than the panel it was opened from — the detail panel is exactly that.
 */
interface OrderTicketPrintProps {
  /** Non-null triggers the print. */
  order: Order | null;
  onPrinted: () => void;
}

function formatMoney(amount: number | undefined, currency: string): string {
  return `${currency} ${Number(amount ?? 0).toFixed(2)}`;
}

export function OrderTicketPrint({ order, onPrinted }: OrderTicketPrintProps) {
  const { t } = useTranslation('orders');

  useEffect(() => {
    if (!order) return;
    // One frame so the ticket is in the DOM before the (synchronous, blocking) print dialog opens.
    const frame = requestAnimationFrame(() => {
      window.print();
      onPrinted();
    });
    return () => cancelAnimationFrame(frame);
    // onPrinted is a fresh closure on every render of the host; keying on the order is what matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order]);

  if (!order) return null;

  const channelKey = isSellChannel(order.channel) ? channelLabelKey(order.channel) : null;
  const placedAt = order.createdAt ? format(new Date(order.createdAt), 'dd/MM/yyyy HH:mm') : null;
  // `discountAmount` carries items AND fee money (C4) AND the loyalty line; the fee share is printed
  // on the fee line and the loyalty money on its own row, so the generic row is what is left.
  const feeDiscount = Number(order.deliveryFeeDiscountAmount ?? 0);
  const loyalty = loyaltyRedemptionOf(order);
  const itemsDiscount = itemsDiscountExcludingLoyalty(order, loyalty);
  const loyaltyTraceId = loyalty?.transactionId ?? loyalty?.reservationId;

  return createPortal(
    <div className="order-ticket-print hidden print:block" aria-hidden="true">
      <h1 className="ticket-title">{t('ticket.title', { id: order.id.slice(0, 8) })}</h1>
      <dl className="ticket-meta">
        {placedAt && (
          <>
            <dt>{t('ticket.placedAt')}</dt>
            <dd>{placedAt}</dd>
          </>
        )}
        {channelKey && (
          <>
            <dt>{t('ticket.channel')}</dt>
            <dd>{t(`board.channel.${channelKey}`)}</dd>
          </>
        )}
        {order.deliveryType && (
          <>
            <dt>{t('ticket.fulfillment')}</dt>
            <dd>{t(`detail.${order.deliveryType}`)}</dd>
          </>
        )}
        <>
          <dt>{t('ticket.customer')}</dt>
          <dd>{orderCustomerName(order) || t('list.noName')}</dd>
        </>
        {order.customer?.phone && (
          <>
            <dt>{t('ticket.phone')}</dt>
            <dd>{order.customer.phone}</dd>
          </>
        )}
        {order.locationName && (
          <>
            <dt>{t('ticket.location')}</dt>
            <dd>{order.locationName}</dd>
          </>
        )}
      </dl>

      <table className="ticket-items">
        <thead>
          <tr>
            <th>{t('ticket.quantity')}</th>
            <th>{t('ticket.item')}</th>
            <th>{t('ticket.lineTotal')}</th>
          </tr>
        </thead>
        <tbody>
          {order.items.map((item, index) => (
            <tr key={`${item.catalogItemId}-${index}`}>
              <td>{item.quantity}×</td>
              <td>
                {item.name}
                {!!item.options?.length && (
                  <span className="ticket-options">{item.options.map((option) => option.optionName).join(' · ')}</span>
                )}
                {typeof item.comments === 'string' && item.comments && (
                  <span className="ticket-options">{item.comments}</span>
                )}
                {Array.isArray(item.comments) &&
                  item.comments.map((comment, commentIndex) => (
                    <span key={commentIndex} className="ticket-options">
                      {comment.quantity}× {comment.text}
                    </span>
                  ))}
              </td>
              <td>{formatMoney(item.subtotal, order.currency)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <dl className="ticket-totals">
        {!!order.deliveryFee && (
          <>
            <dt>{t('detail.deliveryFee')}</dt>
            {/* "Envío $X −$Y", or "gratis" when the discounts cleared it; the discount row below is
                then the items share only, so the ticket still adds up to its total. */}
            <dd>
              {feeDiscount <= 0
                ? formatMoney(order.deliveryFee, order.currency)
                : feeDiscount >= order.deliveryFee
                  ? t('detail.free')
                  : `${formatMoney(order.deliveryFee, order.currency)} −${formatMoney(feeDiscount, order.currency)}`}
            </dd>
          </>
        )}
        {itemsDiscount > 0 && (
          <>
            <dt>{t('ticket.discount')}</dt>
            <dd>-{formatMoney(itemsDiscount, order.currency)}</dd>
          </>
        )}
        {loyalty && loyalty.appliedAmount > 0 && (
          <>
            <dt>
              {t(loyaltyRedemptionLabelKey(loyalty.kind))}
              {loyaltyTraceId && (
                <span className="ticket-options">{t('loyalty.ticketTrace', { id: shortTraceId(loyaltyTraceId) })}</span>
              )}
            </dt>
            <dd>-{formatMoney(loyalty.appliedAmount, order.currency)}</dd>
          </>
        )}
        {!!order.tipAmount && (
          <>
            <dt>{t('detail.tip')}</dt>
            <dd>{formatMoney(order.tipAmount, order.currency)}</dd>
          </>
        )}
        <>
          <dt className="ticket-total">{t('detail.total')}</dt>
          <dd className="ticket-total">{formatMoney(order.total, order.currency)}</dd>
        </>
      </dl>

      {order.notes && <p className="ticket-notes">{order.notes}</p>}
    </div>,
    document.body,
  );
}
